import React, { useCallback, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { useDispatch } from 'react-redux';

// Components
import { Typography } from '../Typography';
import { ButtonTabs } from '../../inputs/buttons/TabsButton';

// Types
import { dataTabs, tabsProps } from './types';

// Components styled
import {
  Container,
  Button,
  ContainerSections,
  Section,
  ContainerTabsButtons,
  Li,
  Ul,
  ContainerUl,
} from './styles';

// Actions
import { tabs } from '../../../state/actions/ui/tabs';
import { useTheme } from 'styled-components';

export default function Tabs({
  data,
  defaultIndex,
  hasButtons,
  typeTab,
  setSelectedIndexTab,
  onUserClick,
  isFirstRemoved,
}: tabsProps): JSX.Element {
  const [selectedIndex, setSelectedIndex] = useState<number>(defaultIndex);
  const { breakPoints, percentBaseResponsive } = useTheme();
  const dispatch = useDispatch();

  const handleClick = useCallback((index: number) => {
    setSelectedIndex(index);
    // Notificar SOLO en click manual del usuario (no en mount ni en sync por defaultIndex).
    onUserClick?.(index);
  }, [onUserClick]);

  // FIX BUG #35: Sincronizar selectedIndex cuando defaultIndex cambie externamente (ej: desde Redux)
  // Esto es necesario porque useState(defaultIndex) solo se inicializa en el primer render
  useEffect(() => {
    if (defaultIndex !== selectedIndex) {
      setSelectedIndex(defaultIndex);
    }
  }, [defaultIndex]);

  useEffect(() => {
    dispatch(tabs(typeTab, selectedIndex));
    setSelectedIndexTab && setSelectedIndexTab(selectedIndex);
  }, [selectedIndex]);

  const getNextEnabledIndex = useCallback(
    (currentIndex: number, step: number): number => {
      const nextIndex = (currentIndex + step + data.length) % data.length;
      if (data[nextIndex].isDisabled) {
        return getNextEnabledIndex(nextIndex, step);
      } else {
        return nextIndex;
      }
    },
    []
  );

  const handleMoveRight = useCallback(() => {
    setSelectedIndex((prevIndex) => getNextEnabledIndex(prevIndex, 1));
  }, []);

  const handleMoveLeft = useCallback(() => {
    setSelectedIndex((prevIndex) => getNextEnabledIndex(prevIndex, -1));
  }, []);

  // 🔴 El corte es `landscapeTablet` (1023 px), NO `portraitTablet` (600).
  // Con 600 una tablet REAL en vertical (800 px de ancho) se quedaba en UNA
  // fila: las 5 pestañas de Órdenes de trabajo piden 833 px de texto en un
  // hueco de 736 y la última, "Multimedia", salía cortada con su marco de
  // seleccionada. Medido a 800 px. A partir de 1024 (tablet apaisada y
  // escritorio) sigue en una fila, que ahí cabe de sobra.
  const splitInTwoRows = useMedia(
    `(max-width: ${breakPoints.landscapeTablet * percentBaseResponsive}rem)`
  );

  // Reparto en 2 filas en tablet vertical (rework 2026-07-29). Antes la fila de
  // arriba llevaba SIEMPRE los 2 primeros tabs y la de abajo el resto → con 3
  // tabs (p.ej. Recorrido de vigilancia) la 3ª quedaba SOLA a todo el ancho
  // ("Multimedia" gigante). Con `floor(N/2)` arriba las dos filas quedan
  // equilibradas: 3→1+2 (el 1º, que además suele ser el label largo, arriba a
  // todo el ancho; los 2 cortos abajo), 4→2+2, 5→2+3 (WorkOrders NO cambia),
  // 6→3+3. Con ≤2 tabs se quedan en UNA fila (no se parten).
  //
  // 🔴 El reparto cuenta las pestañas VISIBLES, no las totales (2026-08-15).
  // La primera se renderiza pero va oculta cuando `isFirstRemoved` (formularios
  // de edición). Contando el total, con 3 pestañas salía `floor(3/2) = 1`: la
  // fila de arriba se llevaba solo el índice 0 —el oculto— y quedaba un `<ul>`
  // CON BORDE y sin nada dentro, que se colapsa a una línea horizontal encima
  // de las pestañas. Se veía en tablet y en web móvil; en escritorio no, porque
  // ahí no se parte en dos filas.
  const hiddenFirst = isFirstRemoved ? 1 : 0;
  const visibleCount = data.length - hiddenFirst;
  const firstRowCount =
    visibleCount <= 2
      ? data.length // caben en UNA fila: la segunda no se renderiza
      : hiddenFirst + Math.floor(visibleCount / 2);

  const renderTabs = useCallback(
    (item: dataTabs, index: number) => (
      <Li
        key={'tab-title-item-' + index}
        hidden={index === 0 && isFirstRemoved}
      >
        <Button
          isActive={selectedIndex === index}
          data-active={selectedIndex === index}
          type={'button'}
          onClick={() => handleClick(index)}
          disabled={item.isDisabled}
        >
          <Typography
            size={'S'}
            variant={
              selectedIndex === index ||
                (selectedIndex !== index && !item.isDisabled)
                ? 'semiBold'
                : 'medium'
            }
            colorText={
              selectedIndex === index
                ? 'black'
                : item.isDisabled
                  ? 'greySoftLight'
                  : 'greyVeryDark'
            }
          >
            {item.label}
          </Typography>
        </Button>
      </Li>
    ),
    [selectedIndex]
  );

  return (
    <Container>
      <ContainerTabsButtons>
        {hasButtons ? (
          <ButtonTabs onClick={handleMoveLeft} variant="left" />
        ) : null}
        {splitInTwoRows ? (
          <ContainerUl>
            <Ul>
              {data.map(
                (item, index) => index < firstRowCount && renderTabs(item, index)
              )}
            </Ul>
            {data.length > firstRowCount && (
              <Ul>
                {data.map(
                  (item, index) =>
                    index >= firstRowCount && renderTabs(item, index)
                )}
              </Ul>
            )}
          </ContainerUl>
        ) : (
          <Ul>{data.map((item, index) => renderTabs(item, index))}</Ul>
        )}
        {hasButtons ? (
          <ButtonTabs onClick={handleMoveRight} variant="right" />
        ) : null}
      </ContainerTabsButtons>
      <ContainerSections>
        <Section key={'tabPanel-item-' + selectedIndex}>
          {data[selectedIndex]?.content}
        </Section>
      </ContainerSections>
    </Container>
  );
}
